import React from "react";
import { Box, Card, Chip, Stack, Typography, Button, Divider } from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { PALETTE, ApprovalRow, statusTint, studentName, initials, objectiveLabel } from "../types";
import MasteryProgression from "./MasteryProgression";
import MasteryCriteriaSummary from "./MasteryCriteriaSummary";

const fmt = (iso?: string | null) => iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—";

const MasteryApprovalCard: React.FC<{
  row: ApprovalRow;
  onReview: (r: ApprovalRow) => void;
  onApprove?: (r: ApprovalRow) => void;
  onReject?: (r: ApprovalRow) => void;
  actionsBusy?: boolean;
}> = ({ row, onReview, onApprove, onReject, actionsBusy }) => {
  const tone = statusTint(row.status);
  const name = studentName(row);
  const isPending = row.status === "pending";
  const statusLabel = row.status === "pending" ? "Pending Review" : row.status === "approved" ? "Approved" : "Rejected";

  return (
    <Card sx={{
      borderRadius: 2.5, border: `1px solid ${PALETTE.border}`, boxShadow: "none",
      overflow: "hidden", position: "relative",
      "&::before": { content: '""', position: "absolute", left: 0, top: 0, bottom: 0, width: 3, bgcolor: tone.accent },
    }}>
      <Box sx={{ p: 2.25 }}>
        {/* Student + status row */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: "50%", bgcolor: PALETTE.info.bg, color: PALETTE.navy, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 13, flexShrink: 0 }}>
              {initials(name)}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 15, fontWeight: 800, color: PALETTE.ink }}>{name}</Typography>
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25, flexWrap: "wrap" }}>
                {row.studentGrade && (
                  <Chip size="small" icon={<SchoolOutlinedIcon />} label={`Grade ${row.studentGrade}`} sx={{ bgcolor: "#f1f5f9", color: PALETTE.slate, fontWeight: 700, fontSize: 10.5, height: 20 }} />
                )}
                <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>Raised {fmt(row.created_at)}</Typography>
              </Stack>
            </Box>
          </Stack>
          <Chip label={statusLabel} size="small" sx={{ bgcolor: tone.bg, color: tone.fg, border: `1px solid ${tone.accent}`, fontWeight: 800, fontSize: 11, letterSpacing: "0.02em" }} />
        </Stack>

        {/* Program / target / workflow row */}
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 1.5 }}>
          <Field icon={<MenuBookOutlinedIcon fontSize="small" />} label="Program">{row.programName || "—"}</Field>
          <Field icon={<PersonOutlineIcon fontSize="small" />} label="Target">{row.targetName || `Target #${row.targetId}`}</Field>
          <Field icon={<AccountTreeOutlinedIcon fontSize="small" />} label="Workflow">
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: "wrap" }}>
              <Typography component="span" sx={{ fontSize: 13, fontWeight: 700, color: PALETTE.ink }}>{row.workflowName || "—"}</Typography>
              <Typography component="span" sx={{ fontSize: 11, color: PALETTE.slate }}>· {objectiveLabel(row)}</Typography>
            </Stack>
          </Field>
        </Stack>

        {/* Progression */}
        <Box sx={{ p: 1.5, bgcolor: "#fafcff", border: `1px solid ${PALETTE.border}`, borderRadius: 1.5, mb: 1.5 }}>
          <Typography sx={{ fontSize: 10.5, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", color: PALETTE.slate, mb: 0.75 }}>Proposed Progression</Typography>
          <MasteryProgression row={row} />
        </Box>

        {/* Criteria (objective-aware) */}
        <MasteryCriteriaSummary criteria={row.criteria} />

        {/* Decision context (approved/rejected only) */}
        {!isPending && (row.reviewedByName || row.reviewedAt || row.reviewNotes) && (
          <>
            <Divider sx={{ my: 1.5, borderColor: PALETTE.border }} />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "flex-start" }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 10.5, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", color: PALETTE.slate, mb: 0.25 }}>{row.status === "approved" ? "Approved by" : "Rejected by"}</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: PALETTE.ink }}>{row.reviewedByName || "—"}</Typography>
                <Typography sx={{ fontSize: 11.5, color: PALETTE.slate }}>{fmt(row.reviewedAt)}</Typography>
              </Box>
              {row.reviewNotes && (
                <Box sx={{ flex: 2, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 10.5, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", color: PALETTE.slate, mb: 0.25 }}>Note</Typography>
                  <Typography sx={{ fontSize: 12.5, color: PALETTE.ink, whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{row.reviewNotes}</Typography>
                </Box>
              )}
            </Stack>
          </>
        )}
      </Box>

      {/* Action row */}
      <Box sx={{ px: 2.25, py: 1.5, borderTop: `1px solid ${PALETTE.border}`, bgcolor: "#fbfcfe" }}>
        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ flexWrap: "wrap", gap: 1 }}>
          <Button
            size="small"
            variant="text"
            startIcon={<VisibilityOutlinedIcon />}
            onClick={() => onReview(row)}
            sx={{ textTransform: "none", fontWeight: 700, color: PALETTE.navy }}
          >
            Review Details
          </Button>
          {isPending && onReject && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<CancelOutlinedIcon />}
              onClick={() => onReject(row)}
              disabled={actionsBusy}
              sx={{ textTransform: "none", fontWeight: 800, color: PALETTE.rejected.fg, borderColor: PALETTE.rejected.accent, "&:hover": { bgcolor: PALETTE.rejected.bg, borderColor: PALETTE.rejected.fg } }}
            >
              Reject
            </Button>
          )}
          {isPending && onApprove && (
            <Button
              size="small"
              variant="contained"
              startIcon={<CheckCircleOutlineIcon />}
              onClick={() => onApprove(row)}
              disabled={actionsBusy}
              sx={{ textTransform: "none", fontWeight: 800, bgcolor: PALETTE.approved.accent, color: "#fff", "&:hover": { bgcolor: PALETTE.approved.fg } }}
            >
              Approve
            </Button>
          )}
        </Stack>
      </Box>
    </Card>
  );
};

const Field: React.FC<{ icon: React.ReactNode; label: string; children: React.ReactNode }> = ({ icon, label, children }) => (
  <Box sx={{ flex: 1, minWidth: 0 }}>
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: PALETTE.slate, mb: 0.25 }}>
      {icon}
      <Typography sx={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</Typography>
    </Stack>
    <Box sx={{ fontSize: 13, color: PALETTE.ink, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis" }}>{children}</Box>
  </Box>
);

export default MasteryApprovalCard;
